import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, ShoppingCart, User, LogOut, BookOpen, Receipt, LayoutDashboard } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useCart } from "../lib/cart";
import { Button } from "./ui/button";
import Logo from "./Logo";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "./ui/dropdown-menu";

const CATEGORIES = [
  { slug: "", name: "All" },
  { slug: "ebooks", name: "Ebooks" },
  { slug: "templates", name: "Templates" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { cart } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [q, setQ] = React.useState("");

  const onSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    navigate(`/browse?${params.toString()}`);
  };

  return (
    <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-xl border-b border-border">
      <div className="container-page flex items-center gap-4 h-24 md:h-28">
        <Link to="/" data-testid="logo-link" className="flex items-center shrink-0">
          <Logo className="h-20 md:h-24" />
        </Link>

        <form onSubmit={onSearch} className="flex-1 max-w-xl mx-auto hidden md:flex" data-testid="search-form">
          <div className="flex w-full items-center gap-2 bg-muted/70 rounded-sm px-3 py-1.5 border border-border focus-within:border-primary transition-colors">
            <Search className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
            <input
              data-testid="search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search the catalogue…"
              className="flex-1 bg-transparent outline-none text-sm placeholder:text-muted-foreground"
            />
            <button type="submit" data-testid="search-submit" className="text-xs font-medium px-2 py-1 rounded-sm bg-primary text-primary-foreground hover:opacity-90">Search</button>
          </div>
        </form>

        <div className="flex items-center gap-1">
          <Link to="/cart" data-testid="nav-cart" className="relative flex items-center gap-1 text-sm font-medium px-3 py-2 hover:text-accent transition-colors">
            <ShoppingCart className="h-5 w-5" strokeWidth={1.5} />
            <span className="hidden sm:inline">Cart</span>
            {cart?.items?.length > 0 && (
              <span data-testid="cart-count" className="absolute -top-1 -right-2 bg-primary text-primary-foreground text-[10px] font-bold rounded-full h-4 min-w-4 px-1 flex items-center justify-center">
                {cart.items.length}
              </span>
            )}
          </Link>

          {user?.roles?.includes("admin") && (
            <Button asChild size="sm" className="rounded-sm mr-1" data-testid="nav-admin-dashboard">
              <Link to="/admin"><LayoutDashboard className="h-4 w-4 sm:mr-2" strokeWidth={1.5} /><span className="hidden sm:inline">Admin dashboard</span></Link>
            </Button>
          )}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" data-testid="user-menu-trigger" className="rounded-sm">
                  <User className="h-4 w-4 mr-2" strokeWidth={1.5} />
                  <span className="hidden sm:inline">{user.name || user.email}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-sm">
                <DropdownMenuLabel className="font-normal text-xs text-muted-foreground">{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/library")} data-testid="menu-library">
                  <BookOpen className="h-4 w-4 mr-2" strokeWidth={1.5} /> My library
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/orders")} data-testid="menu-orders">
                  <Receipt className="h-4 w-4 mr-2" strokeWidth={1.5} /> Orders
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout">
                  <LogOut className="h-4 w-4 mr-2" strokeWidth={1.5} /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button asChild variant="ghost" size="sm" className="rounded-sm" data-testid="nav-login">
                <Link to="/login">Sign in</Link>
              </Button>
              <Button asChild size="sm" className="rounded-sm" data-testid="nav-signup">
                <Link to="/signup">Create account</Link>
              </Button>
            </>
          )}
        </div>
      </div>

      <nav className="hidden md:block border-t border-border bg-background">
        <div className="container-page flex items-center gap-6 overflow-x-auto py-2.5 text-sm">
          {CATEGORIES.map((c) => {
            const active = location.pathname === "/browse" && (new URLSearchParams(location.search).get("category") || "") === c.slug;
            return (
              <Link
                key={c.slug || "all"}
                to={c.slug ? `/browse?category=${c.slug}` : "/browse"}
                data-testid={`nav-category-${c.slug || "all"}`}
                className={`whitespace-nowrap font-medium tracking-wide hover:text-accent transition-colors ${active ? "text-accent" : "text-muted-foreground"}`}
              >
                {c.name}
              </Link>
            );
          })}
          <Link to="/bundles" data-testid="nav-bundles" className={`whitespace-nowrap font-medium tracking-wide hover:text-accent transition-colors ${location.pathname.startsWith("/bundles") ? "text-accent" : "text-muted-foreground"}`}>
            Bundles
          </Link>
          <Link to="/blog" data-testid="nav-blog" className={`whitespace-nowrap font-medium tracking-wide hover:text-accent transition-colors ${location.pathname.startsWith("/blog") ? "text-accent" : "text-muted-foreground"}`}>
            Journal
          </Link>
          <Link to="/club" data-testid="nav-club" className={`whitespace-nowrap font-medium tracking-wide hover:text-accent transition-colors italic ${location.pathname === "/club" ? "text-accent" : "text-muted-foreground"}`}>
            Between The Lines
          </Link>
        </div>
      </nav>
    </header>
  );
}
